import { Howler, Howl } from 'howler'
import EventEmitter from 'events'
import { ParsedSrt, WorkWithParsedSrts } from '../pages/api/work/[id]/index'
import { average, stdDev } from './statHelpers'
import { requestTimeDifference } from './apiClient'
import { logger } from './logger'

// Logging vars
const loggingFlag = true

const API_ROOT = '/api'

const webAudioPort = new EventEmitter()
const subtitlePort = new EventEmitter()
const progressPort = new EventEmitter()
const engineStatusPort = new EventEmitter()

declare global {
  interface Window {
    webkitAudioContext: typeof AudioContext
  }
}

// Create the scheduler structure
export default {
  // eslint-disable-next-line
  log: (text: string): void => { if (loggingFlag) logger.info(text) },

  // When the scheduler was created in "local" time
  initTime: 0,
  initPerfTime: 0,
  initializing: false,
  initialized: false,
  WAContext: null,

  // Event emitter
  ports: {
    webAudio: webAudioPort,
    subtitles: subtitlePort,
    progressBar: progressPort,
    engineStatus: engineStatusPort,
  },

  // Variables for clock synchronization
  serverTimeOffset: 0,
  syncCycles: 20,

  // Whether or not the scheduler is currently active
  running: false, // Deactivating terminates subtitle looping etc
  finishing: false,

  // General metadata about the currently selected media
  duration: null,
  id: null,
  title: null,
  loopingWork: true,
  beyondDuration: false,

  // When the last cycle started
  loopStart: 0,

  // Current caption location
  currentIndex: 0,

  // Subtitles
  events: null,

  // Audio Context fields
  audioContext: null,
  audioPlaying: false,
  audioBuffering: false,
  audioBufferError: false,

  audioProcessID: 0,
  nextChunk: null,

  runProcessID: 0,

  silentHowl: null,
  bluetoothLatency: 100,

  isPollingForNewTimestamp: false,
  qeuedRunCallback: null,

  contextStateChangeHandler() {
    if (this.audioContext.state === 'suspended' || this.audioContext.state === 'interrupted') {
      this.audioContext.resume()
    }
  },
  // Should only be run when the page loads.
  async init(): Promise<void> {
    if (!this.initialized && !this.initializing) {
      this.initializing = true
      // Set the clocks
      this.log('Initializing scheduler.')
      this.initTime = Date.now()
      this.initPerfTime = performance.now()

      window.localStorage.setItem('newInitialization', `${this.getCurrentTime()}`)
      window.addEventListener('storage', (event) => {
        if (event.storageArea !== localStorage) return
        if (event.key === 'newInitialization') {
          this.log('we opened in a separate tab!')
          this.handleAudioTransport('stop')
          if (this.audioContext.state !== 'closed') this.audioContext.close()
          if (this.silentHowl) this.silentHowl.unload()
        }
      })

      // Start up the audio context
      this.WAContext = window.AudioContext || window.webkitAudioContext
      this.audioContext = new this.WAContext()
      this.audioContext.onstatechange = this.contextStateChangeHandler.bind(this)
      await this.ntpSync()
      this.initializing = false
      this.initialized = true
    } else if (this.initializing) {
      this.log('Scheduler is being initialized elsewhere.')
      // Poll self to see when initialization has completed.
      await new Promise<void>((resolve) => {
        const intervalID = setInterval(() => {
          if (this.initialized) {
            resolve()
            clearInterval(intervalID)
          }
        }, 10)
      })
    } else {
      this.log('Scheduler already initialized.')
    }
  },

  // Get the data for a show and start running!
  async load({
    id, parsedSrts, audioUrl: workAudioUrl, duration, title, nudge, dilation, loopingWork, tracks,
  }: WorkWithParsedSrts): Promise<void> {
    this.log(`Loading a new work: ${title}`)
    if (this.running) this.finishing = true
    this.running = false // This termiantes all the awaited functions that are running

    this.id = id // Set the new id to load.
    this.loopingWork = loopingWork
    this.nudge = (nudge === null) ? 0 : nudge
    this.dilation = (dilation === null) ? 1 : dilation

    if (!this.events) {
      const defaultCaptions = parsedSrts?.filter((data) => data.parsedSrt)[0]
      this.loadCaptions(defaultCaptions)
    }
    this.scaleCaptions() // Force captions to be scaled in case dilation was still null when loaded

    this.duration = duration

    const audioUrl = tracks?.[0]?.audioUrl ? tracks[0].audioUrl : workAudioUrl

    if (this.audioPlaying && this.audiofile !== audioUrl) {
      this.audioPlaying = false
      this.destroyGraph()
      this.reportWebAudioStatus()
    }
    this.audiofile = audioUrl

    if (audioUrl && !this.audioPlaying) {
      this.audioPlaying = false
      this.audioBuffering = false
      this.reportWebAudioStatus()
    }
    // The first time the show launches, it should wait for a new time stamp

    if (this.finishing) {
      if (this.qeuedRunCallback) this.ports.engineStatus.off('finished', this.qeuedRunCallback)
      this.qeuedRunCallback = async () => {
        await this.startWhenReady()
      }
      this.ports.engineStatus.once('finished', this.qeuedRunCallback)
    } else {
      await this.startWhenReady()
    }
  },

  loadCaptions(captions: ParsedSrt): void {
    if (captions?.parsedSrt) {
      this.currentIndex = 0
      this.parsedSrt = captions.parsedSrt // load the new data
      this.scaleCaptions()
    } else {
      this.events = [{
        id: 0, start: 0, end: Math.max(), text: 'No text available.',
      }]
    }
  },

  scaleCaptions(): void {
    this.events = this.parsedSrt?.map(({
      id: eventId, start, end, text,
    }) => {
      const startMS = start * (this.dilation ? 1000 : 1)
      const endMS = end * (this.dilation ? 1000 : 1)
      const table = {
        id: eventId, start: startMS, end: endMS, text,
      }
      return table
    })
    if (!this.events) {
      this.events = [{
        id: 0, start: 0, end: Math.max(), text: 'No text available.',
      }]
    }
  },

  async startWhenReady(): Promise<void> {
    this.running = true // enable the awaited functions
    await this.pollForNewTimestamp(20)
    if (!this.loopingWork && this.beyondDuration) {
      this.setDisplay(`Waiting to begin... ${this.audioFile ? '<br>When prompted, press the <i>Speaker</i> icon to turn audio on.' : ''}`)
      let startAfterWhileLoop = true
      while (this.beyondDuration) {
        await this.pollForNewTimestamp(100)
        if (this.finishing && !this.running) {
          this.finishing = false
          this.ports.engineStatus.emit('finished')
          startAfterWhileLoop = false
          break
        }
      }
      if (startAfterWhileLoop) this.run(this.runProcessID) // go
    } else {
      this.run(this.runProcessID) // go
    }
  },

  determineNextMinute(offset: number): number {
    const minute = (Math.floor(this.getLocation() / 60000) + offset)
    return minute
  },

  async fetchNextChunk(offset: number): Promise<void> {
    this.audioBuffering = true
    this.reportWebAudioStatus()
    this.nextChunk = null
    delete this.nextChunk
    const nextMinute = this.determineNextMinute(offset)
    const pathToGet = `${this.audiofile.substring(0, this.audiofile.length - 4)}-${String(this.audioContext.sampleRate).substring(0, 2)}kHz-${String(nextMinute).padStart(3, '0')}.mp3`
    this.log(`Attempt to fetch chunk: ${pathToGet}`)
    const fileResponse = await fetch(pathToGet)
    const arrayBuffer = await fileResponse.arrayBuffer()
    const decodedAudio = await new Promise((resolve, reject) => {
      this.audioContext.decodeAudioData(arrayBuffer, (buf) => resolve(buf), (err) => reject(err))
    })
    this.nextChunk = decodedAudio
    this.audioBuffering = false
    this.reportWebAudioStatus()
  },

  playNextChunk(id: number): void {
    if (this.nextChunk) {
      this.destroyGraph('old')
      this.shiftGraph()
      this.setupGraph()
      const location = (this.getLocation() % 60000) / 1000 + this.bluetoothLatency / 1000.0
      this.graph.current.bufferNode.start(0, location)
      this.graph.current.gainNode.gain.linearRampToValueAtTime(1, this.audioContext.currentTime + 1)
      this.nextChunk = null
      delete this.nextChunk
      const nextMin = this.determineNextMinute(1)
      if (nextMin < Math.ceil(this.duration / 60000)) {
        this.fetchNextChunk(1).then(async () => {
          const threshold = nextMin * 60000
          await this.waitToCross(threshold)
          if (this.audioProcessID === id && this.audioPlaying) this.playNextChunk(id)
        })
      }
    }
  },

  setupGraph(): void {
    this.graph = this.graph ? this.graph : {}
    this.graph.current = {}
    this.graph.current.bufferNode = this.audioContext.createBufferSource()
    this.graph.current.gainNode = this.audioContext.createGain()
    this.graph.current.bufferNode.buffer = this.nextChunk
    this.graph.current.gainNode.gain.value = 0
    this.graph.current.gainNode.gain.setValueAtTime(0, 0)
    this.graph.current.bufferNode.connect(this.graph.current.gainNode)
    this.graph.current.gainNode.connect(this.audioContext.destination)
  },

  shiftGraph(): void {
    if (this.graph?.current) {
      this.log('initiated fade out')
      this.graph.current.gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 1)
      this.graph.old = this.graph.current
      // this.graph.current.gainNode = null
      // this.graph.current.bufferNode = null
      // delete this.graph.current.gainNode
      // delete this.graph.current.bufferNode
      // delete this.graph.current
    } else {
      this.graph = {
        old: {
          bufferNode: null,
          gainNode: null,
        },
      }
    }
  },

  destroyGraph(name: string): void {
    switch (name) {
      case 'old':
        if (this.graph?.old) {
          this.graph.old.bufferNode?.stop()
          this.graph.old.gainNode?.disconnect()
          this.graph.old.bufferNode?.disconnect()
          this.graph.old.gainNode = null
          this.graph.old.bufferNode = null
          delete this.graph.old.gainNode
          delete this.graph.old.bufferNode
          delete this.graph.old
        }
        break
      case 'current':
        if (this.graph?.current) {
          this.graph.current.bufferNode.stop()
          this.graph.current.gainNode.disconnect()
          this.graph.current.bufferNode.disconnect()
          this.graph.current.gainNode = null
          this.graph.current.bufferNode = null
          delete this.graph.current.gainNode
          delete this.graph.currentbufferNode
          delete this.graph.current
        }
        break
      default:
        this.audioProcessID += 1
        this.destroyGraph('old')
        this.destroyGraph('current')
    }
  },

  // Control audio playback state
  handleAudioTransport(state: string): void {
    this.log('web audio transport called')
    if (this.audioContext.state === 'suspended' || this.audioContext.state === 'interrupted') {
      this.log('attempting to restart audio context')
      this.audioContext.resume()
      this.log(`new audiocontext status: ${this.audioContext.state}`)
      this.reportWebAudioStatus()
    }
    if (!this.silentHowl) {
      Howler.autoUnlock = true

      // Create a silent howl
      this.silentHowl = new Howl({
        src: `/audio/keepAlive_${String(this.audioContext.sampleRate).substring(0, 2)}kHz.mp3`,
        html5: true,
        autoplay: true,
        loop: true,
        onunlock: () => {
          this.log('audio unlocked.')
          if (!this.silentHowl.playing()) this.silentHowl.play()
        },
        onload: () => {
          if (!this.silentHowl.playing()) this.silentHowl.play()
        },
      })
    }
    if (!this.silentHowl.playing()) this.silentHowl.play()
    switch (state) {
      case 'toggle': {
        if (this.audioPlaying) this.handleAudioTransport('stop')
        else this.handleAudioTransport('play')
        break
      }
      case 'play': {
        const startMin = this.determineNextMinute(0)
        this.fetchNextChunk(0).then(() => {
          if (this.determineNextMinute(0) === startMin) {
            this.playNextChunk(this.audioProcessID)
            this.audioPlaying = true
            this.reportWebAudioStatus()
          } else {
            this.handleAudioTransport('play')
          }
        })
        break
      }
      case 'stop': {
        if (this.audioPlaying) {
          this.log('Stopping audio.')
          this.destroyGraph()
          this.audioPlaying = false
          this.reportWebAudioStatus()
        }
        break
      }
      default: {
        this.log('Unknown command')
        break
      }
    }
  },

  reportWebAudioStatus(): void {
    this.ports.webAudio.emit('audio context', this.audioContext.state)
    this.ports.webAudio.emit('output latency', this.audioContext.outputLatency)
    this.ports.webAudio.emit('sample rate', this.audioContext.sampleRate)
    this.ports.webAudio.emit('buffering', this.audioBuffering)
    this.ports.webAudio.emit('playing', this.audioPlaying)
    this.ports.webAudio.emit('buffer error', this.audioBufferError)
  },

  // Run the show.
  async run(id: number): Promise<void> {
    this.log('Starting up...')

    // Make sure the display is cleared.
    this.setDisplay('...')

    if (this.audioPlaying && this.audioShouldLoop) {
      this.handleAudioTransport('play')
      this.audioShouldLoop = false
    }
    // Schedule an event but don't await it
    this.log('Scheduling an ntp sync asynchronously')
    this.waitToCross(this.duration - 5000)
      .then(() => {
        if (this.running) {
          this.log('Running a scheduled ntp sync')
          this.ntpSync()
        } else {
          // Consume scheduled event
          this.log('Ignoring a scheduled ntp sync')
        }
      })

    // Identify where we are in the show.
    this.log(`Last Cycle Started at: ${new Date(this.loopStart).toLocaleTimeString('en-US')}`)
    this.log(`Joining show at: ${this.getLocation()}`)
    this.currentIndex = 0
    while (
      (this.getLocation() >= this.events[this.currentIndex]?.end)
      && (this.currentIndex < (this.events.length - 2))
    ) {
      this.currentIndex++
    }
    this.log(`Seeking to cue ${this.currentIndex}.`)

    // As long as the cue point index is less than the total number of cues
    // and as long as the event is actually running...
    while (this.currentIndex < this.events.length && this.running) {
      // Wait until we cross a cue point, then change the display
      await this.waitToCross(this.events[this.currentIndex].start)
      this.log(`Time is beyond start of cue point ${this.currentIndex}`)
      // Make sure events has not changed in the meantime
      if (!this.events[this.currentIndex]) break
      this.setDisplay(this.events[this.currentIndex].text)

      // Wait until we cross the end of the cue point,
      // then update the display and the cue point index.
      await this.waitToCross(this.events[this.currentIndex].end)
      // Make sure events has not changed in the meantime
      if (!this.events[this.currentIndex]) break
      this.log(`Time is beyond end of cue point ${this.currentIndex}`)
      this.setDisplay(' - ')
      this.currentIndex++
    }

    // Once the while loop finishes, we are passed the last cue point
    this.log('passed last cue point.  waiting to finish...')

    await this.waitToFinish()
    this.log('beginning finishing cycle')

    await this.pollForNewTimestamp(30)
    this.log('timestamp poll has completed!')

    if (this.running && this.runProcessID === id && this.loopingWork) {
      this.setDisplay('Waiting to begin...')
      this.log('Recurring loop runner!')
      if (this.audioPlaying) this.audioShouldLoop = true
      this.run(id)
    } else {
      if (!this.loopingWork) {
        this.setDisplay('Work has finished.  Please exit this page.')
        this.running = false
      }
      this.log('Terminating loop.')
      this.runProcessID += 1
      this.loopStart = null
      this.finishing = false
      this.ports.engineStatus.emit('finished')
    }
  },

  // Wait to cross immediately exits if the scheduler gets turned off.
  async waitToCross(cueTime: number): Promise<void> {
    let passedCue = false
    // stop waiting when we pass a cuepoint or the scheduler is turned off
    while (!passedCue && this.running) {
      passedCue = await new Promise((resolve) => (
        setTimeout(() => resolve(this.getLocation() > cueTime), 0)
      ))
    }
  },

  // same as wait to cross but does not use modulus time for finish point
  async waitToFinish(): Promise<void> {
    let passedCue = false
    // stop waiting when we pass a cuepoint or the scheduler is turned off
    while (!passedCue && this.running) {
      passedCue = await new Promise((resolve) => (
        setTimeout(() => resolve(this.getSyncedTime() - this.loopStart > this.duration), 0)
      ))
    }
  },

  // Produce the current time using a monotonic timer (unsynced).
  getCurrentTime(): number {
    return this.initTime + performance.now() - this.initPerfTime
  },

  // Add the server time offset to the current time
  getSyncedTime(): number {
    return this.getCurrentTime() + this.serverTimeOffset + this.nudge
  },

  // Use the synchronized time to determine current location within work
  getLocation(): number {
    return ((this.getSyncedTime() - this.loopStart) % this.duration)
  },

  getPhase(): number {
    const phase = this.getLocation() / this.duration
    return (Number.isNaN(phase)) ? 0 : phase
  },

  // Wait to proceed until a new timestamp has been posted.
  async pollForNewTimestamp(freq: number): Promise<void> {
    this.log('Polling for new timestamp...')
    this.isPollingForNewTimestamp = true
    let fetchedTimestamp = this.loopStart
    let attemptsLeft = 10
    while (fetchedTimestamp <= this.loopStart && this.running && attemptsLeft > 0) {
      // inactive works will only be successfully fetched if user has session (i.e., is an admin)
      const result = await new Promise<Response>((resolve) => setTimeout(() => resolve(fetch(`${API_ROOT}/work/${this.id}?includeInactive=true`)), freq))
      const data = await result.json()
      fetchedTimestamp = data.lastTimestamp
      attemptsLeft -= 1
    }
    if (this.getSyncedTime() - fetchedTimestamp > this.duration) {
      if (this.loopingWork) {
        fetchedTimestamp = this.getSyncedTime() - this.getLocation()
        this.beyondDuration = false
      } else {
        this.beyondDuration = true
      }
    } else {
      this.beyondDuration = false
    }
    if (this.running) this.loopStart = fetchedTimestamp
    this.isPollingForNewTimestamp = false
  },

  // Update the text on screen
  setDisplay(text: string): void {
    this.ports.subtitles.emit('subtitle', text)
  },

  // Can be awaited or set to run in background
  async ntpSync(): Promise<void> {
    this.log('Synchronizing to time server.')
    const offsets = []
    for (let i = 0; i < 20; i++) {
      const data = await requestTimeDifference(this.getCurrentTime.bind(this))
      offsets.push(data.offset)
    }
    const avg = average(offsets)
    const standardDeviation = stdDev(offsets)
    const offsetsNoOutliers = []
    offsets.forEach((offset) => {
      if (Math.abs(offset - avg) < standardDeviation) offsetsNoOutliers.push(offset)
    })
    const averageNoOutliers = average(offsetsNoOutliers)
    // not yet implemented
    this.serverTimeOffset = averageNoOutliers
    this.log(`Server Time Offset: ${this.serverTimeOffset}ms`)
  },
}
